<!--
 * A Vue component that wraps the b-form-input element of Bootstrap Vue.
 * This element is required by default.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for input element.
 *
 * - description -
 * (Optional) A description that appears below the input element.
 *
 * - text -
 * (Optional) The text to show on the label above the input field.
 *
 * - state -
 * (Optional) The validation state to set from parent component. state === true => Valid state
 *                                                               state === false => Invalid state
 *
 * - invalidMessage -
 * (Optional) The text to show when state is set to false (invalid).
 *
 * - labelSize -
 * (Optional) To control the label text size. Valid values are 'lg' for large and 'sm' for small.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 * - min -
 * (Optional) To set the minimum number value.
 *
 * - max -
 * (Optional) To set the maximum number value.
 *
  * - allowDecimals -
 * (Optional) Set to true to allow decimal input.
 *
 * - step -
 * (Optional) To set the increment/decrement step size.
 * If allowDecimals is true and step = 0  then "any" step will be allowed.
 *
 * - maxLength -
 * (Optional) To set the length of the input.
 *
 * Events
 * ==========
 * - input -
 * This event is sent when user changes the input value.
 * The object to sent along with this event is the inserted value from input element.
 *
 * Usage
 * ==========
 * -You do not need to listen to the emitted input event from this component.
 * Just use the v-model in your parent component and the value will be updated
 * from this component automatically.
 *
 *-You can put the standard html input attributes, sucht as required,
 * autofocus, pattern, etc. directly from the parent component without explicit binding.
 * The custom attributes like listed above, should be bind.
-->

<template>
    <div>
        <b-form-group
            :description="description"
            :label="text"
            :label-for="name"
            :label-class="labelClass"
            :invalid-feedback="$_invalidMessage()"
            :state="state"
            :label-size="labelSize"
            :label-cols="labelCols"
        >
            <b-form-input
                v-bind="$attrs"
                ref="inputfield"
                :id="id"
                :name="name"
                type="number"
                class="form-control"
                :value="value"
                :min="minValue"
                :max="maxValue"
                :step="_step"
                :state="$_state()"
                :size="labelSize"
                :formatter="_restrictLength"
                @input="_onInput"
                @change="_onChange"
                @keydown="_checkNumberEntry"
                :required="required"
            >
            </b-form-input>
        </b-form-group>
    </div>
</template>

<script>
    export default {
        inheritAttrs: false,
        name: 'x-number',
        props: {
            description: { type: String, required: false },
            allowDecimals: { type: Boolean, required: false, default: false },
            allowScientificNotation: {
                type: Boolean,
                required: false,
                default: false
            },
            id: { type: String, required: false, default: 'x-number-input' },
            name: { type: String, required: false, default: 'x-number-input' },
            text: { type: String, required: false, default: '' },
            labelSize: { type: String, required: false, default: '' },
            value: { type: Number, required: false, default: 0 },
            defaultValue: { type: Number, required: false, default: null },
            minValue: { type: Number, required: false, default: -999999999 },
            maxValue: { type: Number, required: false, default: 999999999 },
            step: { type: Number, required: false, default: 1 },
            maxLength: { type: Number, required: false, default: 9 },
            invalidMessage: { type: String, default: '' },
            state: { type: Boolean, default: null },
            labelClass: { type: String, default: 'font-weight-normal' },
            labelCols: { type: Number, default: 0 },
            required: { type: Boolean, default: true }
        },
        data: function () {
            return {
                thisState: null,
                thisInValidMessage: null
            };
        },
        computed: {
            _step() {
                // Allow "any" step when working with decimal numbers.
                // allowDecimals = true and incoming step === 0 return "any"
                let result;
                if (this.allowDecimals && this.step === 0) {
                    result = 'any';
                } else {
                    result = this.step;
                }
                return result;
            }
        },

        methods: {
            _normalizeValue: function (input) {
                if (input === '') {
                    return null;
                }

                if (this.allowDecimals) {
                    return Number(input);
                }
                if (typeof input === 'string') {
                    return parseInt(input);
                }

                return input;
            },

            _onChange: function (input) {
                const normalizedValue = this._normalizeValue(input);
                if (normalizedValue === null && this.defaultValue !== null) {
                    this.$emit('input', this.defaultValue);
                }
                this.$emit('change', normalizedValue);
            },

            _validRange(value) {
                this.thisState = true;
                this.thisInvalidMessage = null;

                if (!this.required && !value) {
                    return this.thisState;
                }

                if (this.minValue && value < this.minValue) {
                    this.thisState = false;
                    // value to small
                    // is there a maxvalue message is about range
                    if (this.maxValue) {
                        this.thisInvalidMessage = this.$t('invalidRangeValue', [
                            this.minValue,
                            this.maxValue
                        ]);
                    } else {
                        // otherwise meesage is about minValue alone
                        this.thisInvalidMessage = this.$t('invalidMinValue', [
                            this.minValue
                        ]);
                    }
                }

                if (this.maxValue && value > this.maxValue) {
                    this.thisState = false;
                    // value to small
                    // is there a minvalue message is about range
                    if (this.minValue) {
                        this.thisInvalidMessage = this.$t('invalidRangeValue', [
                            this.minValue,
                            this.maxValue
                        ]);
                    } else {
                        // otherwise meesage is about maxValue alone
                        this.thisInvalidMessage = this.$t('invalidMaxValue', [
                            this.maxValue
                        ]);
                    }
                }
                return this.thisState;
            },
            _onInput: function (input) {
                const value = this._normalizeValue(input);
                // check value against possible min and max range.
                if (this._validRange(value)) {
                    this.$emit('input', value);
                }
            },
            _restrictLength: function (value) {
                let max = this.maxLength;
                // If number starts with - (minus) add 1 to allowed length
                if (value.charAt(0) === '-') {
                    max++;
                }
                return value.slice(0, max);
            },
            _checkNumberEntry: function (event) {
                const suppressedKeys = new Set();

                // Suppress scientific number notation containing +, e or E
                if (!this.allowScientificNotation) {
                    suppressedKeys.add('+');
                    suppressedKeys.add('e');
                    suppressedKeys.add('E');
                }

                // If no decimals allowed suppress . (dot) and , (comma)
                if (!this.allowDecimals) {
                    suppressedKeys.add('.');
                    suppressedKeys.add(',');
                }

                // if minvalue > -1 then suppress -
                if (this.minValue > -1) {
                    suppressedKeys.add('-');
                }

                // Prevent event when invalid key pressed
                if (suppressedKeys.has(event.key)) {
                    event.preventDefault();
                }
            },
            $_state() {
                return this.state ?? this.thisState;
            },
            $_invalidMessage() {
                if (this.invalidMessage) {
                    return this.invalidMessage;
                } else {
                    return this.thisInvalidMessage;
                }
            }
        }
    };
</script>
